import { faPlus, faTrashCan } from '@fortawesome/pro-regular-svg-icons'
import { ActionIcon, Box, Button, Card, Flex, Text } from '@mantine/core'
import { useEffect } from 'react'

import { useAnalytics } from '@flecto/analytics'
import { useTranslation } from '@flecto/translations'
import { type CoreCustomer } from '@flecto/types'
import {
  BadgeCustomerVerification,
  DataTable,
  type DataTableColumn,
  Icon,
} from '@flecto/ui'

import { TextCustomerContacts } from '../../../customers'
import { useModalCustomerSelect } from '../../../insurances/hooks/useModalCustomerSelect'
import { useFormContext } from '../FormCreateInsurancePolicy/FormCreateInsurancePolicy'

export function CardCustomerSelect() {
  const track = useAnalytics('segment')
  const form = useFormContext()
  const { t } = useTranslation('saas', { keyPrefix: 'insurances' })

  const { openModal } = useModalCustomerSelect({
    setCustomer: form.setFieldValue,
  })

  const columns: DataTableColumn<CoreCustomer>[] = [
    {
      accessor: 'full_name',
      width: 250,
      title: t('insurances_details.label_customer_name'),
      render: (row) => <Text>{row.full_name ?? '-'}</Text>,
    },
    {
      accessor: 'contacts',
      title: t('insurances_details.label_customer_contacts'),
      render: (row) => <TextCustomerContacts customer={row} />,
    },
    {
      accessor: 'vat_number',
      title: t('insurances_details.label_customer_vat_number'),
      render: (row) => <Text>{row.vat_number ?? '-'}</Text>,
    },
    {
      accessor: 'identification',
      title: t('insurances_details.label_customer_identification'),
      render: (row) => <BadgeCustomerVerification customer={row} />,
    },
    {
      accessor: 'actions',
      title: '',
      render: () => (
        <Flex justify='flex-end'>
          <ActionIcon
            size='xs'
            onClick={() => {
              form.setFieldValue('customer', undefined)
              form.setFieldValue('customer_id', undefined)
            }}
          >
            <Icon icon={faTrashCan} />
          </ActionIcon>
        </Flex>
      ),
    },
  ]

  useEffect(() => {
    if (form.values?.customer) {
      form.setFieldValue('customer_id', form.values.customer.id)
    }
  }, [form.values?.customer])

  return (
    <Card withBorder>
      <Flex align='center' justify='space-between' mb='lg'>
        <Box style={{ flex: 1 }}>
          <Text fw={500}>{t('create_insurance.select_customer.title')}</Text>
          <Text c='dimmed' size='sm'>
            {t('create_insurance.select_customer.description')}
          </Text>
        </Box>
      </Flex>
      {Boolean(form.values?.customer) && (
        <DataTable
          withBorder={false}
          columns={columns}
          records={form.values.customer ? [form.values.customer] : []}
        />
      )}
      {!form.values.customer && (
        <Button
          data-testid='customer-add-button'
          variant='outline'
          leftSection={<Icon icon={faPlus} />}
          onClick={() => {
            openModal()
            track('INSURANCE_FORM__CUSTOMER_NEW', {})
          }}
        >
          {t('create_insurance.select_customer.select_customer_button')}
        </Button>
      )}
    </Card>
  )
}
